import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToPricing } from "@/components/portfolio/PortfolioNav";

const ACP_SRCSET =
  "/images/openaccess-hero-640.webp 640w, /images/openaccess-hero-1024.webp 1024w, /images/openaccess-hero-1920.webp 1920w";

export default function PortfolioHero() {
  return (
    <section data-testid="pf-hero" className="relative overflow-hidden flex flex-col lg:min-h-screen">
      {/* Version ordinateur : image plein écran avec titre intégré */}
      <div className="absolute inset-0 hidden lg:block">
        <img
          src="/images/openaccess-hero-1920.webp"
          srcSet={ACP_SRCSET}
          sizes="100vw"
          alt="Investisseur face à ses écrans de trading, titre OPENACCESS"
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover object-[50%_15%]"
        />
      </div>
      <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-[#111111]/85 to-transparent hidden lg:block" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/40 via-[#111111]/20 to-[#111111] hidden lg:block" aria-hidden="true" />

      {/* Version mobile / tablette : image entière en haut, fondu vers le fond */}
      <div className="relative w-full mt-16 lg:hidden">
        <img
          src="/images/openaccess-hero-640.webp"
          srcSet={ACP_SRCSET}
          sizes="100vw"
          alt="Investisseur face à ses écrans de trading, titre OPENACCESS"
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-auto block"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-b from-transparent to-[#111111]" aria-hidden="true" />
      </div>

      <h1 className="sr-only" data-testid="pf-hero-title">OPENACCESS</h1>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-10 md:pb-14 mt-8 lg:mt-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex flex-wrap gap-2 lg:gap-3 mb-6"
        >
          <span className="micro-label" data-testid="pf-hero-label-1">Portefeuille réel</span>
          <span className="micro-label" data-testid="pf-hero-label-2">Mouvements publiés</span>
          <span className="micro-label !border-[#D92D20]/50 !text-[#FF6B00]" data-testid="pf-hero-label-3">
            <span className="animate-pulse-line">29 € / mois</span>
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="font-display italic text-xl md:text-2xl text-[#FF6B00]"
          data-testid="pf-hero-subtitle"
        >
          Rentre au cœur de mon activité d'investisseur.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="max-w-[65ch] text-[#D8D4CC] font-light mt-4"
        >
          Sur les réseaux, je partage ma lecture du monde. Ici, je montre ce que j'en fais
          avec mon propre argent : toutes mes positions, chaque achat, chaque vente et
          pourquoi, ainsi que mes analyses régulières.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-4"
        >
          <button
            type="button"
            onClick={scrollToPricing}
            data-testid="pf-hero-cta-button"
            className="bg-[#D92D20] hover:bg-[#b3241a] text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 rounded-sm transition-[background-color,transform] duration-300 hover:scale-[1.02] text-center w-full md:w-auto"
          >
            S'abonner
          </button>
          <a
            href="#contenu"
            data-testid="pf-hero-secondary-button"
            className="border border-white/20 hover:bg-white/5 text-[#F5F5F0] font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 rounded-sm transition-colors duration-300 text-center w-full md:w-auto"
          >
            Voir le contenu
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 md:right-12 z-10 hidden lg:flex items-center gap-2 text-[#758D85]"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Défiler</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
